import cn from 'classnames';
import React from 'react';

import {AppButton} from '@/components/Button';
import RemoteIcon from '@/components/Icon/RemoteIcon';
import {MaxWidthContainer} from '@/components/Layout';
import PageHeaderMedia from '@/components/PageHeaderMedia/PageHeaderMedia';
import InstallPwaLanguageSelect from '@/containers/InstallPwaPage/components/InstallPwaLanguageSelect';
import useInstallPwaButton from '@/containers/InstallPwaPage/hooks/useInstallPwaButton';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import {getAriaLabel} from '@/services/accessibilityService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import marketConfigService from '@/services/marketConfigService';
import appRouterService from '@/services/route/appRouterService';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

import InstallPwaPageContent from './components/InstallPwaPageContent';
import useInstalledAppRedirection from './useInstalledAppRedirection';

const InstallPwaPage = () => {
    const localizedStrings = getLocalizedStrings();
    const currentGetStartedPage = marketConfigService.getGetStartedPage();

    const {page, icons, skipButton, installButton} = currentGetStartedPage || {};
    const {titleKey, descriptionKey, imageMedia, videoPath, videoThumbnailPath} = page || {};
    const title = localizedStrings[titleKey];
    const skipButtonAriaLabel = getAriaLabel(skipButton?.altKey);
    const installButtonAriaLabel = getAriaLabel(installButton?.altKey);

    useDocumentTitle(title);
    useInstalledAppRedirection();

    const {showInstallButton, onInstallClick} = useInstallPwaButton();

    const onClick = () => {
        appRouterService.forwardToLoginPage();
    };

    return (
        <div className='flex w-full flex-col bg-primary-dark-5'>
            <div className='flex w-full flex-1 flex-col'>
                <WarningBannersTop source={WARNING_BANNER_SOURCE_TYPES.INSTALL_PWA} />
                <InstallPwaLanguageSelect />
                <div className='flex h-full w-full flex-col items-center justify-items-center pt-0-hw-offset ml:mx-8 ml:grid ml:max-w-[1024px] ml:grid-cols-[1fr_1fr] ml:grid-rows-[auto_auto] ml:gap-x-8 ml:self-center'>
                    <MobileScreenHeightContainer showInstallButton={showInstallButton}>
                        <PageHeaderMedia
                            className='min-h-0 px-16 ml:order-1 ml:row-span-2 ml:px-0'
                            autoPlay
                            loop
                            playsInline
                            isVideoAvailable={!!videoThumbnailPath}
                            videoClassName='ml:rounded-[24px]'
                            videoContainerClassName='relative'
                            imgClassName='relative pt-12'
                            videoThumbnailSrc={videoThumbnailPath}
                            videoSrc={videoPath}
                            imageMedia={imageMedia}
                            content={[{isControlsAvailable: false}]}
                        />
                        <MaxWidthContainer
                            className={cn('m-8 flex flex-col items-center ml:m-0 ml:mb-8 ml:self-end', {
                                'grow ml:items-start': !showInstallButton,
                            })}
                            size='ml'
                        >
                            <InstallPwaPageContent
                                title={title}
                                descriptionKey={descriptionKey}
                                icons={icons}
                                showInstallButton={showInstallButton}
                            />
                            {showInstallButton && (
                                <AppButton
                                    secondary
                                    dark
                                    className='mt-8 w-full'
                                    onClick={onInstallClick}
                                    aria-label={installButtonAriaLabel}
                                >
                                    <span>{localizedStrings[installButton?.titleKey]}</span>
                                </AppButton>
                            )}
                        </MaxWidthContainer>
                    </MobileScreenHeightContainer>

                    <div className={cn('order-2 flex ml:w-full ml:self-start', {'justify-center': showInstallButton})}>
                        <AppButton
                            className={cn(
                                'm-0 py-[10px] text-14 underline shadow-none ml:px-8 ml:py-0 ml:text-16 ml:no-underline',
                                {'py-0 no-underline': showInstallButton}
                            )}
                            dark
                            onClick={onClick}
                            aria-label={skipButtonAriaLabel}
                        >
                            <span>{localizedStrings[skipButton?.titleKey]}</span>
                            <div className={cn('ml-3 inline', {'hidden ml:inline': !showInstallButton})}>
                                <RemoteIcon className='inline fill-primary pb-1 ml:pb-0.5' icon={skipButton} />
                            </div>
                        </AppButton>
                    </div>
                </div>
            </div>
            <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.INSTALL_PWA} />
        </div>
    );
};

const MobileScreenHeightContainer = ({children, showInstallButton}) => {
    const isMobileLayout = useIsMobileLayout();

    return isMobileLayout && !showInstallButton ? <div className='min-h-svh'>{children}</div> : <>{children}</>;
};

export default InstallPwaPage;
